'use client';

import { cn } from '@kit/ui/utils';

import { useNativePresentation } from '~/(tenant)/_components/native/_lib/use-native-presentation';

import { useState, useTransition } from 'react';
import { useTranslation } from 'react-i18next';
import { Calendar, Clock, User } from 'lucide-react';
import { buildPaymentDescription } from '@kit/payments/description';

import { formatCurrency } from '@kit/shared/currency';
import { formatLocaleDate, formatTimeToAmPm } from '@kit/shared/utils';
import { CurrencyIcon, useTenantCurrency } from '@kit/ui/currency-icon';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@kit/ui/dialog';
import { Button } from '@kit/ui/button';
import { toast } from '@kit/ui/sonner';
import { Trans } from '@kit/ui/trans';

import { MoyasarCheckoutDialog } from '../../book/_components/moyasar-checkout-dialog';
import { ApplePayPackageRenewalButton } from '../../payment/_components/apple-pay-package-renewal-button';
import { createFixedSlotPackageRenewalCheckoutAction } from '../_lib/server/renew-fixed-slot-package-action';

interface FixedSlotMeta {
  days_of_week: string[] | null;
  start_time: string | null;
  end_time: string | null;
  last_session_date: string | null;
  trainer_id: string | null;
  trainer_name: string | null;
}

interface RenewPackageDialogProps {
  isOpen: boolean;
  onClose: () => void;
  userPackageId: string;
  packageName: string;
  renewalPrice: number | null;
  slotDuration: number;
  fixedSlot: FixedSlotMeta | null;
}

export function RenewPackageDialog({
  isOpen,
  onClose,
  userPackageId,
  packageName,
  renewalPrice,
  slotDuration,
  fixedSlot,
}: RenewPackageDialogProps) {
  const presentation = useNativePresentation();
  const currency = useTenantCurrency();
  const { t, i18n } = useTranslation('packages');
  const [isSubmitting, startSubmit] = useTransition();
  const [moyasarCheckoutData, setMoyasarCheckoutData] = useState<
    React.ComponentProps<typeof MoyasarCheckoutDialog>['checkoutData']
  >(null);

  const days = fixedSlot?.days_of_week ?? [];
  const startTime = fixedSlot?.start_time ?? null;
  const endTime = fixedSlot?.end_time ?? null;
  const trainerName = fixedSlot?.trainer_name ?? null;
  const hasRenewalPrice = renewalPrice != null && Number.isFinite(renewalPrice);

  function handleConfirm() {
    if (!hasRenewalPrice) return;

    startSubmit(async () => {
      try {
        const result = await createFixedSlotPackageRenewalCheckoutAction({
          userPackageId,
        });

        if (!result.success) {
          toast.error(result.error ?? t('renew.startFailed'));
          return;
        }

        setMoyasarCheckoutData({
          sessionId: result.data.sessionId,
          provider: result.data.provider,
          purchaseId: result.data.purchaseId,
          publishableKey: result.data.publishableKey,
          userId: result.data.userId,
          playerCount: result.data.playerCount,
          tenantId: result.data.tenantId,
          tenantName: result.data.tenantName,
          locationId: result.data.locationId,
          packageData: result.data.packageData,
          extraMetadata: result.data.metadata,
        });
      } catch (err) {
        toast.error(
          err instanceof Error ? err.message : t('renew.startFailed'),
        );
      }
    });
  }

  const bookingDetails = moyasarCheckoutData
    ? {
        amount: moyasarCheckoutData.packageData?.price ?? 0,
        description: buildPaymentDescription({
          surface: 'package_renewal',
          name: moyasarCheckoutData.packageData?.name,
        }),
      }
    : null;

  // Native Apple Pay button for the renewal, shown alongside the card form
  // inside MoyasarCheckoutDialog for Moyasar tenants once the server action has
  // issued the purchase + credentials — rendered on the same basis as the
  // new-package purchase modal (package-checkout-dialog), so renewal matches
  // that already-shipped flow. The button charges the server amount and
  // forwards the server metadata (renewal schedule + purchase reference) verbatim.
  const renewalCheckout = moyasarCheckoutData;
  const applePaySlot =
    renewalCheckout &&
    renewalCheckout.provider === 'moyasar' &&
    renewalCheckout.publishableKey &&
    renewalCheckout.purchaseId &&
    renewalCheckout.packageData ? (
      <ApplePayPackageRenewalButton
        amount={renewalCheckout.packageData.price}
        label={renewalCheckout.packageData.name}
        publishableKey={renewalCheckout.publishableKey}
        purchaseId={renewalCheckout.purchaseId}
        packageId={renewalCheckout.packageData.id}
        metadata={{
          tenant_id: renewalCheckout.tenantId ?? '',
          tenant_name: renewalCheckout.tenantName ?? '',
          ...(renewalCheckout.extraMetadata ?? {}),
        }}
      />
    ) : undefined;

  return (
    <>
      <Dialog
        open={isOpen && !moyasarCheckoutData}
        onOpenChange={(open) => {
          if (!open) onClose();
        }}
      >
        <DialogContent
          className={cn('max-w-md', presentation.className)}
          style={presentation.style}
        >
          <DialogHeader>
            <DialogTitle>
              <Trans i18nKey="packages:renew.title" defaults="Renew Package" />
            </DialogTitle>
            <DialogDescription>
              <Trans
                i18nKey="packages:renew.description"
                defaults="Renew your package with the same slot before it opens to the public."
              />
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-3 rounded-lg border p-4">
            <div className="font-semibold">{packageName}</div>

            {days.length > 0 && (
              <div className="flex items-start gap-2 text-sm">
                <Calendar className="mt-0.5 h-4 w-4 text-muted-foreground shrink-0" />
                <span>
                  {days.join(', ')}
                  {startTime && endTime && (
                    <>
                      {' · '}
                      {formatTimeToAmPm(startTime, i18n.language)} –{' '}
                      {formatTimeToAmPm(endTime, i18n.language)}
                    </>
                  )}
                </span>
              </div>
            )}

            <div className="flex items-center gap-2 text-sm">
              <Clock className="h-4 w-4 text-muted-foreground" />
              <span>
                <Trans
                  i18nKey="packages:customerView.minSlots"
                  values={{ duration: slotDuration }}
                  defaults="{{duration}} min"
                />
              </span>
            </div>

            {trainerName && (
              <div className="flex items-center gap-2 text-sm">
                <User className="h-4 w-4 text-muted-foreground" />
                <span>{trainerName}</span>
              </div>
            )}

            {fixedSlot?.last_session_date && (
              <div className="text-xs text-muted-foreground">
                <Trans
                  i18nKey="packages:renew.newCycleStartsAfter"
                  values={{
                    date: formatLocaleDate(
                      fixedSlot.last_session_date,
                      i18n.language,
                    ),
                  }}
                  defaults="New cycle starts the week after {{date}}"
                />
              </div>
            )}

            <div className="flex items-center justify-between border-t pt-3">
              <span className="text-sm text-muted-foreground">
                <Trans i18nKey="packages:renew.price" defaults="Price" />
              </span>
              {hasRenewalPrice ? (
                <span className="flex items-center gap-1 text-lg font-semibold">
                  {formatCurrency(renewalPrice, currency, i18n.language, {
                    style: 'decimal',
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  })}
                  <CurrencyIcon className="h-4 w-4" />
                </span>
              ) : (
                <span className="text-sm text-muted-foreground" role="status">
                  <Trans
                    i18nKey="packages:renew.priceUnavailable"
                    defaults="Could not load the renewal price. Please refresh and try again."
                  />
                </span>
              )}
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={onClose} disabled={isSubmitting}>
              <Trans i18nKey="common:cancel" defaults="Cancel" />
            </Button>
            <Button
              onClick={handleConfirm}
              disabled={isSubmitting || !hasRenewalPrice}
            >
              {isSubmitting ? (
                <Trans i18nKey="common:loading" defaults="Loading…" />
              ) : (
                <Trans
                  i18nKey="packages:renew.continueToPayment"
                  defaults="Continue to Payment"
                />
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <MoyasarCheckoutDialog
        isOpen={!!moyasarCheckoutData}
        onClose={() => setMoyasarCheckoutData(null)}
        checkoutData={moyasarCheckoutData}
        bookingDetails={bookingDetails}
        applePaySlot={applePaySlot}
        // This callback runs before 3DS. The return page verifies the renewal.
        onSuccess={() => {
          setMoyasarCheckoutData(null);
          onClose();
        }}
      />
    </>
  );
}
